import { Button } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

/**
 * /features/fees - prerendered feature page targeting searches like
 * "school fees management software Zimbabwe" and "school fees tracking system".
 */
export const FeesFeaturePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/features/fees">
      <SEO
        title="School Fees Management Software for Zimbabwean Schools | Rongeka Africa"
        description="Invoice every student, track arrears by term, send automatic WhatsApp fee reminders, and record payments in one place. Fee income posts straight into your school accounts."
        url="https://rongeka.africa/features/fees"
        keywords="school fees management software, school fees payment system Zimbabwe, fee tracking system, school invoicing software, school arrears management"
      />

      <header className="mkt-hero">
        <div className="landing-section-label">FEES AND PAYMENTS</div>
        <h1 className="mkt-hero-title">School Fees Management Without the Spreadsheets</h1>
        <p className="mkt-hero-lead">
          Know exactly who owes what, this term and from previous terms. Invoice every
          student, remind parents automatically on WhatsApp, and record every payment
          against the right account.
        </p>
        <div className="mkt-hero-actions">
          <Button type="primary" size="large" onClick={() => navigate('/get-started')}>
            Start Your Free Term
          </Button>
        </div>
      </header>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Fee structures that match how your school charges</h2>
          <p className="mkt-section-body">
            Set fees per level, per class, and per term - tuition, levies, and any custom
            charges your school uses. When a new term opens, every student gets the right
            charges automatically, and each student's account keeps current-term fees and
            carried-over arrears clearly separated.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Tuition, levies, and custom charge items per level and term</li>
            <li><CheckCircleOutlined />Per-student fee accounts for every term</li>
            <li><CheckCircleOutlined />Arrears carried forward and tracked separately from current fees</li>
            <li><CheckCircleOutlined />Sibling and per-student adjustments where you need them</li>
          </ul>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Parents always know their balance</h2>
          <p className="mkt-section-body">
            Invoices and statements are delivered straight to guardians on WhatsApp. Automated
            reminders chase outstanding balances so your bursar does not spend the term making
            phone calls - and parents can check their balance themselves any time by messaging
            the school's Rongeka number.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Invoices and fee statements on WhatsApp and email</li>
            <li><CheckCircleOutlined />Automated payment reminders on a schedule you control</li>
            <li><CheckCircleOutlined />Self-service balance checks for parents on WhatsApp</li>
            <li>
              <CheckCircleOutlined />
              <span>
                Online payment with EcoCash -{' '}
                <RouterLink to="/features/ecocash-payments">see how EcoCash payments work</RouterLink>
              </span>
            </li>
          </ul>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Every payment recorded, every report ready</h2>
          <p className="mkt-section-body">
            Record cash, bank, mobile money, and online payments against the student's account
            with a receipt generated on the spot. Collection reports, arrears lists, and
            class-by-class summaries are one click away - and if you use the accounting
            add-on, fee income posts into your books automatically, with no double capture.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Receipts issued automatically for every payment</li>
            <li><CheckCircleOutlined />Arrears and collection reports by class, level, or term</li>
            <li><CheckCircleOutlined />Fee income posts directly into the accounting module</li>
            <li><CheckCircleOutlined />Full audit trail of who recorded what, and when</li>
          </ul>
        </div>
      </section>
    </MarketingLayout>
  );
};
